import axios from "axios";
import { server } from "../server";

const api = axios.create({
  baseURL: server,
});

let accessToken = null;

export const setAccessToken = (token) => (accessToken = token);
export const clearAccessToken = () => (accessToken = null);

api.interceptors.request.use((config) => {
  const token = accessToken || localStorage.getItem("dbToken");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
    if (!accessToken) accessToken = token;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      clearAccessToken();
      localStorage.removeItem("dbToken");
      localStorage.removeItem("user");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error?.response?.data);
  },
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

export const getIpAddresses = async () => {
  const res = await api.get("/ipAddress");
  return res.data;
};

export const createIpAddress = async (data) => {
  const res = await api.post("/ipAddress", data);
  return res.data;
};

export const updateIpAddress = async (id, data) => {
  const res = await api.put(`/ipAddress/${id}`, data);
  return res.data;
};

export const deleteIpAddress = async (id) => {
  const res = await api.delete(`/ipAddress/${id}`);
  return res.data;
};

export const getAllDeviceConfigs = async (pageNo = 1, pageSize = 10) => {
  const res = await api.get("/DeviceConfig/getAll", {
    params: { pageNo, pageSize },
  });
  return res.data;
};

export const getDeviceConfigById = async (ip) => {
  const res = await api.get("/DeviceConfig/getById", {
    params: { ip },
  });
  return res.data;
};

export const addDeviceConfig = async (data) => {
  const res = await api.post("/DeviceConfig/add", data);
  return res.data;
};

export const updateDeviceConfig = async (ip, data) => {
  const res = await api.put("/DeviceConfig/updateById", data, {
    params: { ip },
  });
  return res.data;
};

export const deleteDeviceConfig = async (ip) => {
  const res = await api.delete("/DeviceConfig/deleteById", {
    params: { ip },
  });
  return res.data;
};

export const getDeviceReport = async (ip, type = "daily") => {
  const res = await api.get(`/Report/device/${ip}`, {
    params: { type },
  });
  return res.data;
};

export const getAllReports = async (type, fromDate, toDate, pageNo = 1, pageSize = 10) => {
  const params = { pageNo, pageSize };
  if (type) params.type = type;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;

  const res = await api.get("/networkMonitorApi/all", { params });
  return res.data;
};

export const getDeviceLogs = async (ip, date, page = 1, pageSize = 50) => {
  const res = await api.get(
    `/DeviceLogs?date=${date}&ip=${ip}&page=${page}&pageSize=${pageSize}`,
  );
  return res.data;
};

export const scanSavedDevices = async () => {
  const res = await api.post("/Network/scan/saved-devices");
  return res.data;
};

export const pingDevice = async (ipAddress, timeout = 10000) => {
  const res = await api.post("/Network/ping", { ipAddress, timeout });
  return res.data;
};

export const getMikrotikInterfaces = async (ipAddress) => {
  const res = await api.get("/mikrotik/get-interface", {
    params: { ipAddress },
  });
  return res.data;
};

export const getMikrotikSystemResources = async (ipAddress) => {
  const res = await api.get("/mikrotik/system/resources", {
    params: { ipAddress },
  });
  return res.data;
};

export const addDeviceStock = async (data) => {
  const res = await api.post("/deviceStock/add", data);
  return res.data;
};

export const getAllManufacturers = async (pageNo = 1, pageSize = 100) => {
  const res = await api.get("/manufacturerMaster/getAll", {
    params: { pageNo, pageSize },
  });
  return res.data;
};

export const getAllDeviceTypes = async (pageNo = 1, pageSize = 100) => {
  const res = await api.get("/deviceTypeMaster/getAll", {
    params: { pageNo, pageSize },
  });
  return res.data;
};

export const getAllModels = async (pageNo = 1, pageSize = 100) => {
  const res = await api.get("/modelMaster/getAll", {
    params: { pageNo, pageSize },
  });
  return res.data;
};

export const getAllDeviceStocks = async (pageNo = 1, pageSize = 10) => {
  const res = await api.get("/deviceStock/getAll", {
    params: { pageNo, pageSize },
  });
  return res.data;
};

export const getAllTickets = async (userWise = false, pageNo = 1, pageSize = 10) => {
  const res = await api.get("/tickets/getAll", {
    params: { userWise, pageNo, pageSize },
  });
  return res.data;
};

export const createNewTicket = async (data) => {
  const res = await api.post("/tickets/new", data);
  return res.data;
};

export const addTicketFollowup = async (ticketId, message) => {
  const res = await api.post(`/tickets/${ticketId}/followup`, { message });
  return res.data;
};

export const closeTicket = async (ticketId) => {
  const res = await api.patch(`/tickets/${ticketId}/close`);
  return res.data;
};

export const openTicket = async (ticketId) => {
  const res = await api.patch(`/tickets/${ticketId}/open`);
  return res.data;
};

export const getTicketsReport = async (userWise = false) => {
  const res = await api.get("/tickets/report", {
    params: { userWise },
  });
  return res.data;
};

export default api;
